import * as React from 'react';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import GoogleIcon from '@mui/icons-material/Google';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import { getDeviceId } from '@utils';

interface GoogleConnectProps {
  redirect?: string | null;
}

export function GoogleConnect(props: Readonly<GoogleConnectProps>) {
  const { redirect = null } = props;
  const [disabled, setDisabled] = React.useState(false);
  const { t } = useTranslation('authentication');

  const handleGoogleLogin = async () => {
    const deviceId = await getDeviceId();

    if (!deviceId) {
      setDisabled(true);
      import.meta.env.DEV && console.error('Device ID is required for Google login');
      return;
    }

    const baseUrl = import.meta.env.VITE_OAUTH_API_URI as string;
    const platform = Capacitor.isNativePlatform() ? 'native' : 'web';

    const url = new URL(`${baseUrl}/google`);
    url.searchParams.set('platform', platform);
    url.searchParams.set('deviceId', deviceId);

    if (redirect) url.searchParams.set('redirect', redirect);

    if (platform === 'native') {
      await Browser.open({
        url: url.toString(),
        presentationStyle: 'popover',
      });
      return;
    }

    window.location.href = url.toString();
  };

  return (
    <Button
      fullWidth
      variant="contained"
      size="small"
      onClick={handleGoogleLogin}
      disabled={disabled}
      sx={{
        bgcolor: 'white',
        color: 'black',
        '&:hover': {
          bgcolor: 'white',
        },
      }}
      startIcon={<GoogleIcon />}
    >
      {t('button.Connect with', { provider: 'Google' })}
    </Button>
  );
}
